import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

const socialPlatforms = [
  { id: 'whatsapp', name: 'WhatsApp', icon: '📱', placeholder: '+59170000000' },
  { id: 'facebook', name: 'Facebook', icon: '👤', placeholder: 'facebook.com/tuusuario' },
  { id: 'instagram', name: 'Instagram', icon: '📸', placeholder: '@tuusuario' },
  { id: 'linkedin', name: 'LinkedIn', icon: '💼', placeholder: 'linkedin.com/in/tuusuario' },
  { id: 'tiktok', name: 'TikTok', icon: '🎵', placeholder: '@tuusuario' },
  { id: 'youtube', name: 'YouTube', icon: '📺', placeholder: 'youtube.com/@tuusuario' },
  { id: 'twitter', name: 'Twitter', icon: '🐦', placeholder: '@tuusuario' },
  { id: 'telegram', name: 'Telegram', icon: '✈️', placeholder: '@tuusuario' },
]

function SocialLinksForm({ socialLinks, onSocialLinksChange }) {
  const [selectedPlatform, setSelectedPlatform] = useState('')
  const [newLink, setNewLink] = useState('')

  const handleAddLink = () => {
    if (selectedPlatform && newLink.trim()) {
      onSocialLinksChange({
        ...socialLinks,
        [selectedPlatform]: newLink.trim()
      })
      setSelectedPlatform('')
      setNewLink('')
    }
  }

  const handleRemoveLink = (platform) => {
    const updatedLinks = { ...socialLinks }
    delete updatedLinks[platform]
    onSocialLinksChange(updatedLinks)
  }

  const handleLinkChange = (platform, value) => {
    onSocialLinksChange({
      ...socialLinks,
      [platform]: value
    })
  }

  const getPlatformInfo = (platformId) => {
    return socialPlatforms.find(p => p.id === platformId)
  }

  const getAvailablePlatforms = () => {
    return socialPlatforms.filter(platform => !socialLinks[platform.id])
  }

  return (
    <div className="space-y-6">
      {/* Existing links */}
      {Object.keys(socialLinks).length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Tus Redes Sociales</h3>
          {Object.entries(socialLinks).map(([platform, link]) => {
            const platformInfo = getPlatformInfo(platform)
            return (
              <div key={platform} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <span className="text-2xl">{platformInfo?.icon}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{platformInfo?.name}</p>
                  <input
                    type="text"
                    value={link}
                    onChange={(e) => handleLinkChange(platform, e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder={platformInfo?.placeholder}
                  />
                </div>
                <button
                  onClick={() => handleRemoveLink(platform)}
                  className="flex-shrink-0 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-colors"
                  title="Eliminar"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Add new link */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Agregar Red Social</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plataforma
            </label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">Selecciona una plataforma</option>
              {getAvailablePlatforms().map((platform) => (
                <option key={platform.id} value={platform.id}>
                  {platform.icon} {platform.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enlace o Usuario
            </label>
            <input
              type="text"
              value={newLink}
              onChange={(e) => setNewLink(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder={
                selectedPlatform
                  ? getPlatformInfo(selectedPlatform)?.placeholder
                  : 'Ingresa tu usuario o enlace'
              }
              disabled={!selectedPlatform}
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleAddLink}
              disabled={!selectedPlatform || !newLink.trim()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Agregar
            </button>
          </div>
        </div>
      </div>

      {/* Quick tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Consejos rápidos:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Para WhatsApp: usa el formato internacional (+59170000000)</li>
          <li>• Para Instagram/TikTok: usa @tunombredeusuario</li>
          <li>• Para Facebook/LinkedIn: puedes usar solo tu nombre de usuario</li>
          <li>• Los enlaces se mostrarán como botones en tu vCard</li>
        </ul>
      </div>
    </div>
  )
}

SocialLinksForm.propTypes = {
  socialLinks: PropTypes.object.isRequired,
  onSocialLinksChange: PropTypes.func.isRequired,
}

export default SocialLinksForm
